import {
    ExpenseLedgerAccounts,
    UncategorizedLedgerAccount,
    getLedgerAccountOptionLabel
} from "common/LedgerAccounts";
import React, { useEffect, useMemo, useState } from "react";
// @ts-ignore
import Select from "react-select";
import { useOfficeTranslation } from "i18n/OfficeTranslation";

interface LedgerOption {
    value: string;
    label: string;
}

interface Props {
    value: string;
    onChange: (value: string) => void;
    onSave?: (value: string) => void;
    inputId?: string;
    compact?: boolean;
}

const LedgerAccountSelect: React.FC<Props> = ({ value, onChange, onSave, inputId, compact }) => {
    const { t } = useOfficeTranslation();
    const options = useMemo<LedgerOption[]>(() => [
        { value: UncategorizedLedgerAccount, label: t("ledgerAccounts.uncategorized") },
        ...ExpenseLedgerAccounts.map(account => ({
            value: account.value,
            label: getLedgerAccountOptionLabel(account, t),
        })),
    ], [t]);

    const [selectedValue, setSelectedValue] = useState(value || UncategorizedLedgerAccount);

    useEffect(() => {
        setSelectedValue(value || UncategorizedLedgerAccount);
    }, [value]);

    const selectedOption = options.find(option => option.value === selectedValue) || options[0];

    const commit = (nextValue: string) => {
        setSelectedValue(nextValue);
        onChange(nextValue);
        if (onSave) {
            onSave(nextValue);
        }
    };

    return (
        <Select
            inputId={inputId}
            classNamePrefix="ledger-select"
            value={selectedOption}
            options={options}
            isSearchable={true}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            placeholder={t("ledgerAccounts.search")}
            noOptionsMessage={() => t("ledgerAccounts.noOptions")}
            onChange={(option: LedgerOption | null) => {
                if (option) {
                    commit(option.value);
                }
            }}
            onKeyDown={(event: React.KeyboardEvent) => {
                if (event.key === "Enter") {
                    onSave?.(selectedValue);
                }
            }}
            styles={{
                menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                control: (base: any) => ({
                    ...base,
                    minHeight: compact ? 32 : 38,
                    borderColor: "#ced4da",
                    boxShadow: "none",
                    "&:hover": {
                        borderColor: "#adb5bd",
                    },
                }),
                valueContainer: (base: any) => ({
                    ...base,
                    paddingTop: compact ? 0 : base.paddingTop,
                    paddingBottom: compact ? 0 : base.paddingBottom,
                }),
                indicatorsContainer: (base: any) => ({
                    ...base,
                    minHeight: compact ? 32 : 38,
                }),
            }}
        />
    );
};

export default LedgerAccountSelect;
